import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  useGetHigherLevelAnalysisColorClarityQuery, 
  useGetShapeSizeDistributionQuery, 
  useGetHigherLevelAnalysisPriceMovementQuery 
} from '../../redux/slices/apiSlice';
import { pricing } from '../../redux/slices/pricingSlice';
import { selectDateRange } from '../../redux/slices/dateRangeSlice';
import debounce from 'lodash/debounce';
import { setSelectedCaratRange, setSelectedShape } from '../../redux/slices/higherOrderSlice';
import { ColorRing } from 'react-loader-spinner';
import HigherLevelGrid from '../higherLevelGrid';
import HigherLevelData from './HigherLevelData';
import PriceMovementData from './PriceMovementData';
import HigherIndividualMovement from './HigherIndividualMovement';

const HigherLevelAnalysis = ({ isCsr, accountDetails }) => {
  const filteredRedux = useSelector(pricing).filteredPerformance;
  const dispatch = useDispatch();
  const dateRange = useSelector(selectDateRange);
  
  // Enhanced state tracking for loading states
  const [diamondKeys, setDiamondKeys] = useState([]);
  const [allCaratRanges, setAllCaratRanges] = useState([]);
  const [isChangingShapeOrCarat, setIsChangingShapeOrCarat] = useState(false);
  const [isChangingGrading, setIsChangingGrading] = useState(false);
  const [isChangingPricing, setIsChangingPricing] = useState(false);
  const [filtered, setFiltered] = useState(filteredRedux);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [minLoadingTimer, setMinLoadingTimer] = useState(false);
  
  // State for tracking parameter changes
  const [prevQueryParams, setPrevQueryParams] = useState(null);
  const [paramChangeTimestamp, setParamChangeTimestamp] = useState(0);
  
  // Query params state with better initialization
  const [queryParams, setQueryParams] = useState({
    id: accountDetails.account_id,
    shape: '0000round',
    caratRange: '2.0-2.19',
    carat_range: '2.0-2.19',
  });
  
  // Track tab configuration for consistent display values
  const tabOptionsConfig = {
    "Shape": {
      prop_name: "shape",
      options: [
        { display: "Round", value: "0000round" },
        { display: "Princess", value: "0001princess" },
        { display: "Pear", value: "0002pear" },
        { display: "Oval", value: "0003oval" },
        { display: "Emerald", value: "0004emerald" },
        { display: "Marquise", value: "0005marquise" },
        { display: "Cushion", value: "0007cushion_brilliant" },
        { display: "Radiant", value: "0009radiant" },
        { display: "Asscher", value: "0011asscher" }
      ]
    },
    "Carat Range": {
      prop_name: "carats",
      options: [
        { display: "10.0-99.99", value: "10.0-99.99" },
        { display: "5.0-5.99", value: "5.0-5.99" },
        { display: "4.0-4.99", value: "4.0-4.99" },
        { display: "3.0-3.99", value: "3.0-3.99" },
        { display: "2.0-2.99", value: "2.0-2.99" },
        { display: "1.5-1.99", value: "1.5-1.99" },
        { display: "1.0-1.49", value: "1.0-1.49" },
        { display: "0.9-0.99", value: "0.9-0.99" },
        { display: "0.7-0.89", value: "0.7-0.89" },
        { display: "0.5-0.69", value: "0.5-0.69" },
        { display: "0.4-0.49", value: "0.4-0.49" },
        { display: "0.3-0.39", value: "0.3-0.39" },
        { display: "0.2-0.29", value: "0.2-0.29" },
        { display: "0-0.19", value: "0-0.19" },
      ]      
    },

    "Color": {
      prop_name: "color",
      options: [
        { display: "D", value: "0000d" },
        { display: "E", value: "0001e" },
        { display: "F", value: "0002f" },
        { display: "G", value: "0003g" },
        { display: "H", value: "0004h" },
        { display: "I", value: "0005i" },
        { display: "J", value: "0006j" },
      ]
    },

    "Clarity": {
      prop_name: "clarity",
      options: [
        { display: "IF", value: "0001if" },
        { display: "VVS1", value: "0002vvs1" },
        { display: "VVS2", value: "0003vvs2" },
        { display: "VS1", value: "0004vs1" },
        { display: "VS2", value: "0005vs2" },
        { display: "SI1", value: "0006si1" },
        { display: "SI2", value: "0007si2" },
        { display: "I1", value: "0009i1" },
        
      ]
    },
    
  };

  // Enhanced change handler with debounce for better performance
  const handleShapeOrCaratChange = useCallback(debounce(() => {
    setIsChangingShapeOrCarat(true);
    setIsChangingGrading(true);
    setIsChangingPricing(true);
    setMinLoadingTimer(true);
    
    // Generate a timestamp for tracking this change
    setParamChangeTimestamp(Date.now());
    
    // Ensure minimum loading time for better UX
    setTimeout(() => {
      setMinLoadingTimer(false);
    }, 500);
  }, 200), []);

  // First API call to get shape/size distribution and ideal turn
  const {
    data: shapeData,
    error: shapeError,
    isLoading: isShapeLoading,
    isFetching: isShapeFetching
  } = useGetShapeSizeDistributionQuery({
    id: accountDetails.account_id,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate
  }, {
    refetchOnMountOrArgChange: true
  });

  // API call for price movement data with better skip handling
  const {
    data: priceMovement,
    error: priceMovementError,
    isLoading: isPriceMovementLoading,
    isFetching: isPriceMovementFetching,
  } = useGetHigherLevelAnalysisPriceMovementQuery(queryParams, {
    skip: !queryParams.caratRange || !initialDataLoaded,
    refetchOnMountOrArgChange: true
  });
  
  // Enhanced loading state tracking
  const isDataLoading = isShapeLoading || isShapeFetching;
  const isPriceDataLoading = isPriceMovementLoading || isPriceMovementFetching || isChangingPricing;
  
  // Comprehensive loading state that includes all possible loading scenarios
  const isCompletelyLoading = 
    isDataLoading || 
    isPriceDataLoading ||
    isChangingShapeOrCarat ||
    isChangingGrading ||
    minLoadingTimer;

  // Enhanced error state tracking
  const hasError = 
    (shapeError && !isDataLoading) || 
    (priceMovementError && !isPriceDataLoading && initialDataLoaded) ||
    (!shapeData && !isDataLoading);

  // Track parameter changes
  useEffect(() => {
    if (prevQueryParams && JSON.stringify(prevQueryParams) !== JSON.stringify(queryParams)) {
      // Parameters have changed, update loading states
      handleShapeOrCaratChange();
    }
    
    // Update previous params for next comparison
    setPrevQueryParams({...queryParams});
  }, [queryParams, prevQueryParams, handleShapeOrCaratChange]);
  
  // Price movement data state
  const [priceMovementColorKeys, setPriceMovementColorKeys] = useState([]);
  const [priceMovementClarityDatas, setPriceMovementClarityDatas] = useState([]);
  const [allpriceMovementClarityRanges, setAllPriceMovementClarityRanges] = useState([]);

  // Process initial shape data
  useEffect(() => {
    if (shapeData?.data) {
      setIsChangingShapeOrCarat(true);
      
      const keys = Object.keys(shapeData.data);
      setDiamondKeys([
        "0000round",
        "0001princess",
        "0002pear",
        "0003oval",
        "0004emerald",
        "0005marquise",
        "0007cushion_brilliant",
        "0009radiant",
        "0011asscher",
      ]);
      // setDiamondKeys([
      //   "0000round",
      //   "0001princess",
      //   "0002pear",
      //   "0003oval",
      //   "0004emerald",
      //   "0005marquise",
      //   "0006heart",
      //   "0007cushion_brilliant",
      //   "0009radiant",
      //   "0010square_radiant",
      //   "0011asscher",
      // ]);
      
      if (keys.length > 0) {
        const caratRanges = [
          "10.0-99.99",
          "5.0-5.99",
          "4.0-4.99",
          "3.0-3.99",
          "2.0-2.99",
          "1.5-1.99",
          "1.0-1.49",
          "0.9-0.99",
          "0.7-0.89",
          "0.5-0.69",
          "0.4-0.49",
          "0.3-0.39",
          "0.2-0.29",
          "0-0.19"
        ];
        // const caratRanges = [
        //   "5.0-99.99",
        //   "4.0-4.99",
        //   "3.5-3.99",
        //   "3.0-3.49",
        //   "2.5-2.99",
        //   "2.2-2.49",
        //   "2.0-2.19",
        //   "1.7-1.99",
        //   "1.5-1.69",
        //   "1.2-1.49",
        //   "1.1-1.19",
        //   "1.0-1.09",
        //   "0.9-0.99",
        //   "0.8-0.89",
        //   "0.7-0.79",
        //   "0.6-0.69",
        //   "0.5-0.59",
        //   "0.4-0.49",
        //   "0.3-0.39",
        //   "0.2-0.29",
        //   "0-0.19"
        // ];
        
        setAllCaratRanges(caratRanges);
        
        if (caratRanges.length > 0) {
          dispatch(setSelectedCaratRange(caratRanges[6]));
          dispatch(setSelectedShape(keys[0]));
          
          // Set initial query params once we have the data
          setQueryParams(prev => ({
            ...prev,
            shape: keys[0],
            caratRange: caratRanges[6],
            carat_range: caratRanges[6],
          }));
          
          // Mark that initial data is now available
          if (!initialDataLoaded) {
            setInitialDataLoaded(true);
          }
        }
      }
      
      // Reset loading state after a short delay for smoother transitions
      setTimeout(() => {
        setIsChangingShapeOrCarat(false);
      }, 200);
    }
  }, [shapeData, dispatch]);

  // Process price movement data with better error handling
  useEffect(() => {
    if (!isPriceMovementLoading && !isPriceMovementFetching && priceMovement?.data) {
      try {
        const keys = Object.keys(priceMovement.data);
        setPriceMovementColorKeys(keys);
        
        const mappedPriceMovementData = Object.values(priceMovement.data).map(
          category => Object.values(category)
        );
        
        const clarityRanges = [];
        keys.forEach(shapeKey => {
          const ranges = Object.keys(priceMovement.data[shapeKey]).filter(
            key => key !== 'trans'
          );
          clarityRanges.push(...ranges);
        });
        
        // Use a Set to ensure unique values
        setAllPriceMovementClarityRanges([...new Set(clarityRanges)]);
        
        const clarityfilterData = mappedPriceMovementData.map(
          category => category.filter(item => typeof item === 'object')
        );
        
        setPriceMovementClarityDatas(clarityfilterData);
        
        // Reset loading states after data is processed
        setIsChangingPricing(false);
      } catch (error) {
        console.error("Error processing price movement data:", error);
        setIsChangingPricing(false);
      }
    }
  }, [priceMovement, isPriceMovementLoading, isPriceMovementFetching]);

  // Reset loading states when data is ready
  useEffect(() => {
    if (initialDataLoaded && shapeData && priceMovement) {
      // Data is loaded, gradually reset loading states
      setTimeout(() => {
        setIsChangingShapeOrCarat(false);
        setIsChangingGrading(false);
        setIsChangingPricing(false);
      }, 300);
    }
  }, [initialDataLoaded, shapeData, priceMovement]);

  return (
    <div className="px-5 py-6 flex flex-col gap-4 h-auto">
      <div className="pagetitle text-center flex justify-center mx-auto">
        <h1 className="w-[675px] text-center">Market Intelligence</h1>
      </div>
      
      {/* Individual Movement Component always visible */}
      <HigherIndividualMovement 
        accountDetails={accountDetails}
        tabOptionsConfig={tabOptionsConfig}
      />
      
      {/* Global Loading State */}
      {isCompletelyLoading && !initialDataLoaded && (
        <div className="flex justify-center items-center h-40">
          <ColorRing
            visible={true}
            height="80"
            width="80"
            ariaLabel="loading-indicator"
            colors={["#00319f", "#2e56b1", "#5c7bc2", "#8aa1d3", "#b2c1e2"]}
          />
        </div>
      )}
      
      {/* Error State */}
      {hasError && !isCompletelyLoading && (
        <div className="flex justify-center text-red-500 py-8">
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <p className="font-medium">Unable to load data. Please try again later.</p>
          </div>
        </div>
      )}
      
      {/* Content Area */}
      <div className="mt-4">
        {/* Shape/Size Grid */}
        {shapeData?.data && initialDataLoaded ? (
          <div className={`transition-opacity duration-300 ${isChangingShapeOrCarat ? 'opacity-50' : 'opacity-100'}`}>
            <HigherLevelGrid
              isCsr={isCsr}
              showLoading={isDataLoading}
              queryParams={queryParams}
              caratRanges={allCaratRanges}
              shapes={diamondKeys}
              accountDetails={accountDetails}
              dateRange={dateRange}
              defaultTurn={shapeData.ideal_turn}
              data={shapeData.data}
              setQueryParams={setQueryParams}
              onShapeOrCaratChange={handleShapeOrCaratChange}
              setIsChangingShapeOrCarat={setIsChangingShapeOrCarat}
              setIsChangingGrading={setIsChangingGrading}
              setIsChangingPricing={setIsChangingPricing}
            />
          </div>
        ) : null}
        
        {/* Price Movement Data */}
        <div className="mt-8">
          {isPriceDataLoading && initialDataLoaded ? (
            <div className="flex justify-center items-center h-40">
              <ColorRing
                visible={true}
                height="60"
                width="60"
                ariaLabel="blocks-loading"
                colors={["#00319f", "#2e56b1", "#5c7bc2", "#8aa1d3", "#b2c1e2"]}
              />
            </div>
          ) : priceMovement?.data && initialDataLoaded ? (
            <div className={`transition-opacity duration-300 ${isChangingPricing ? 'opacity-50' : 'opacity-100'}`}>
              <PriceMovementData
                queryParams={queryParams}
                accountDetails={accountDetails}
                isCsr={isCsr}
                showLoading={isPriceDataLoading}
                defaultTurn={shapeData?.ideal_turn}
                priceMovement={priceMovement}
                priceMovementColorKeys={priceMovementColorKeys}
                priceMovementClarityDatas={priceMovementClarityDatas}
                allpriceMovementClarityRanges={allpriceMovementClarityRanges}
                isPriceMovementLoading={isPriceDataLoading}
                isChangingPricing={isChangingPricing}
                setIsChangingPricing={setIsChangingPricing}
                minLoadingTimer={minLoadingTimer}
                tabOptionsConfig={tabOptionsConfig}
              />
            </div>
          ) : initialDataLoaded ? (
            <div className="flex justify-center py-6">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-gray-600">Market Information not available.</p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default HigherLevelAnalysis;